import React, { useState, useRef, useEffect } from 'react';

const MultiCountySelect = ({ counties, selectedCounties, onChange, disabled, single = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleCounty = (county) => {
    const isSelected = selectedCounties.some(c => c.county_id === county.county_id);
    if (single) {
      // Single-select mode: picking a county replaces the selection and closes the dropdown
      onChange(isSelected ? [] : [county]);
      setIsOpen(false);
      return;
    }
    if (isSelected) {
      onChange(selectedCounties.filter(c => c.county_id !== county.county_id));
    } else {
      onChange([...selectedCounties, county]);
    }
  };

  const selectAll = () => {
    onChange([...counties]);
  };

  const clearAll = () => {
    onChange([]);
  };

  // Filter counties based on search term
  const filteredCounties = counties.filter(county =>
    county.county_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full border rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'
        }`}
      >
        {selectedCounties.length === 0 ? (
          <span className="text-gray-500">{single ? 'Select county...' : 'Select counties...'}</span>
        ) : selectedCounties.length === 1 ? (
          <span>{selectedCounties[0].county_name}</span>
        ) : (
          <span className="text-blue-600 font-medium">{selectedCounties.length} counties selected</span>
        )}
        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          ▼
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search input */}
          <div className="sticky top-0 bg-white border-b px-3 py-2">
            <input
              type="text"
              placeholder="Search counties..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          {/* Select/Clear all buttons (multi-select only) */}
          {!single && (
            <div className="sticky top-[52px] bg-gray-50 border-b px-3 py-2 flex justify-between">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); selectAll(); }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Select All ({counties.length})
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); clearAll(); }}
                className="text-xs text-red-600 hover:text-red-800 font-medium"
              >
                Clear All
              </button>
            </div>
          )}
          
          {/* County list */}
          <div className="max-h-52 overflow-y-auto">
            {filteredCounties.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                No counties found
              </div>
            ) : (
              filteredCounties.map((county) => {
                const isSelected = selectedCounties.some(c => c.county_id === county.county_id);
                return (
                  <div
                    key={county.county_id}
                    onClick={() => toggleCounty(county)}
                    className={`px-3 py-2 cursor-pointer hover:bg-blue-50 flex items-center ${
                      isSelected ? 'bg-blue-100' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="mr-2 h-4 w-4 text-blue-600 rounded"
                    />
                    <span className={isSelected ? 'font-medium text-blue-700' : ''}>
                      {county.county_name}
                    </span>
                  </div>
                );
              })
            )}
          </div>
          
          {/* Selected count footer */}
          {!single && selectedCounties.length > 0 && (
            <div className="sticky bottom-0 bg-blue-50 border-t px-3 py-2 text-xs text-blue-700">
              {selectedCounties.length} county{selectedCounties.length !== 1 ? 'ies' : ''} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiCountySelect;
